import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Layout } from '../components/Layout';
import SEO from '../components/SEO';
import { useTheme } from '../context/ThemeContext';
import {
    Users,
    MessageCircle,
    Calendar,
    Building2,
    BookOpen,
    ArrowRight,
    Globe,
    Heart,
    Shield
} from 'lucide-react';
import { MyEnAbLogo } from '../components/MyEnAbLogo';

const features = [
    {
        icon: Users,
        title: 'Global Community',
        description: 'Connect with people who understand your experiences from around the world.',
        color: 'inclusion-red'
    },
    {
        icon: MessageCircle,
        title: 'Discussion Forums',
        description: 'Share stories, ask questions, and support each other in topic-based forums.',
        color: 'inclusion-gold'
    },
    {
        icon: Building2,
        title: 'Service Directory',
        description: 'Find trusted service providers, NGOs, and support organizations near you.',
        color: 'inclusion-white'
    },
    {
        icon: Calendar,
        title: 'Events & Meetups',
        description: 'Discover virtual and in-person events designed with accessibility in mind.',
        color: 'inclusion-blue'
    },
    {
        icon: BookOpen,
        title: 'Resource Library',
        description: 'Access guides, articles, and tools to help navigate daily challenges.',
        color: 'inclusion-green'
    },
    {
        icon: Heart,
        title: 'Direct Messaging',
        description: 'Build meaningful connections through private, secure conversations.',
        color: 'inclusion-red'
    }
];

// Initial fallback stats
const initialStats = [
    { key: 'users', value: '50K+', label: 'Community Members' },
    { key: 'providers', value: '1,200+', label: 'Service Providers' },
    { key: 'events', value: '500+', label: 'Monthly Events' },
    { key: 'countries', value: '120+', label: 'Countries' },
    { key: 'visits', value: '0', label: 'Site Visits' }
];

const inclusionColors = [
    { name: 'Life (Physical)', color: '#FF5C5C', description: 'Represents the resilience and vitality of the physical disability community.' },
    { name: 'Sunlight (Neurodivergence)', color: '#FFD700', description: 'Symbolizes the bright evolution and diverse minds in neurodivergence.' },
    { name: 'Harmony (Invisible)', color: '#F4F4F5', description: 'Recognizes the vast community of invisible and undiagnosed disabilities.' },
    { name: 'Serenity (Psychiatric)', color: '#38BDF8', description: 'Represents the peace and depth of mental health and psychiatric experiences.' },
    { name: 'Nature (Sensory)', color: '#34D399', description: 'Symbolizes the connection to nature and diverse sensory perceptions.' }
];

export default function Landing() {
    const [appStats, setAppStats] = useState(initialStats);
    const { theme } = useTheme();

    useEffect(() => {
        const fetchStats = async () => {
            try {
                // Record the visit first
                await axios.post(`${API_URL}/stats/visit`).catch(e => console.error('Error recording visit:', e));

                const response = await axios.get(`${API_URL}/stats`);
                const data = response.data;

                // Map backend data to landing page stats
                const updatedStats = initialStats.map(stat => ({
                    ...stat,
                    value: data[stat.key] !== undefined
                        ? (data[stat.key]).toLocaleString() + (data[stat.key] >= 1000 ? '+' : '')
                        : stat.value
                }));

                setAppStats(updatedStats);
            } catch (error) {
                console.error('Error fetching stats:', error);
            }
        };

        fetchStats();
    }, []);

    return (
        <Layout>
            <SEO
                title="Home"
                description="MyEnAb bridges individuals with disabilities, volunteers, service providers, NGOs, and caregivers into one global community — building a world where barriers disappear and every ability flourishes."
                keywords="MyEnAb, disability inclusion, inclusive community, advocacy, accessibility, service providers, NGOs"
            />
            {/* Hero Section */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                {/* Diagonal Inclusion Stripes Background */}
                <div className="absolute inset-0 inclusion-gradient-bg opacity-50"></div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
                    <div className="max-w-3xl">
                        <div className="flex items-center mb-6 animate-fade-up">
                            <MyEnAbLogo className="w-64 h-20" />
                        </div>

                        <h1 className="font-lexend text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
                            Connect. Support. Enable.
                            <span className="block mt-2 bg-gradient-to-r from-[#E40303] via-[#FF8C00] via-[#FFD700] via-[#008026] via-[#24408E] to-[#732982] bg-clip-text text-transparent">
                                MyEnAb
                            </span>
                        </h1>

                        <p className="text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed animate-fade-up" style={{ animationDelay: '0.2s' }}>
                            MyEnAb bridges individuals with disabilities, volunteers, service providers, NGOs, and caregivers into one global community — building a world where barriers disappear and every ability flourishes.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: '0.3s' }}>
                            <Link to="/register">
                                <Button className="btn-primary text-base px-8 py-4 h-auto" data-testid="hero-join-btn">
                                    Join the Community
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/directory">
                                <Button variant="ghost" className="btn-secondary text-base px-8 py-4 h-auto" data-testid="hero-explore-btn">
                                    Explore Services
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Diagonal Cut */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-[#121212] diagonal-cut-reverse"></div>
            </section>


            {/* Features Section */}
            <section className="py-20" data-testid="features-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-lexend text-3xl md:text-4xl font-bold mb-4">
                            Everything You Need to Connect
                        </h2>
                        <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
                            Our platform is designed with accessibility at its core, ensuring everyone can participate fully.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            const colorMap = {
                                'inclusion-red': '#E40303',
                                'inclusion-gold': '#FF8C00',
                                'inclusion-yellow': '#FFD700',
                                'inclusion-green': '#008026',
                                'inclusion-blue': '#24408E',
                                'inclusion-purple': '#732982',
                                'inclusion-white': theme === 'dark' ? '#F4F4F5' : '#52525b'
                            };
                            const accentColor = colorMap[feature.color] || (theme === 'dark' ? '#F4F4F5' : '#52525b');

                            return (
                                <Card
                                    key={feature.title}
                                    className="bg-[#18181B] border-[#27272A] hover:border-white/20 transition-all duration-300 group animate-fade-up"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                    data-testid={`feature-card-${index}`}
                                >
                                    <CardContent className="p-6">
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                                            style={{ backgroundColor: `${accentColor}20` }}
                                        >
                                            <Icon className="w-6 h-6" style={{ color: accentColor }} />
                                        </div>
                                        <h3 className="font-lexend text-xl font-semibold mb-2">{feature.title}</h3>
                                        <p className="text-zinc-400 leading-relaxed">{feature.description}</p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Inclusion Flag Meaning Section */}
            <section className="py-20" data-testid="inclusion-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div
                        className="rounded-3xl p-10 md:p-14"
                        style={{ backgroundColor: '#0a0a0a' }}
                    >
                    <div className="text-center mb-16">
                        <h2 className="font-lexend text-3xl md:text-4xl font-bold mb-4" style={{ color: '#ffffff' }}>
                            The Disability Pride Flag
                        </h2>
                        <p className="text-lg max-w-2xl mx-auto" style={{ color: '#a1a1aa' }}>
                            The Disability Pride Flag symbolizes the diverse experiences of our community.
                            Designed by Ann Magill, each color represents a specific aspect of disability identity,
                            set against a charcoal background that honors our shared history and resilience.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {inclusionColors.map((item, index) => (
                            <div
                                key={item.name}
                                className="relative p-6 rounded-2xl animate-fade-up"
                                style={{
                                    animationDelay: `${index * 0.1}s`,
                                    backgroundColor: '#121212',
                                    border: '1px solid #27272A',
                                }}
                                data-testid={`inclusion-color-${item.name.toLowerCase()}`}
                            >
                                <div
                                    className="w-full h-2 rounded-full mb-4"
                                    style={{ backgroundColor: item.color }}
                                ></div>
                                <h3 className="font-lexend font-semibold text-lg mb-2" style={{ color: item.color }}>
                                    {item.name}
                                </h3>
                                <p className="text-sm" style={{ color: '#a1a1aa' }}>{item.description}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-12 p-8 rounded-2xl" style={{ backgroundColor: '#18181B', border: '1px solid #27272A' }}>
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                                <Shield className="w-6 h-6" style={{ color: '#ffffff' }} />
                            </div>
                            <div>
                                <h3 className="font-lexend font-semibold text-xl mb-2" style={{ color: '#ffffff' }}>The Charcoal Background</h3>
                                <p className="leading-relaxed" style={{ color: '#a1a1aa' }}>
                                    The charcoal or black background of the flag represents mourning for those who have been
                                    lost to ableism, violence, and abuse. The diagonal band represents "cutting across"
                                    the barriers that separate disabled people from mainstream society.
                                </p>
                            </div>
                        </div>
                    </div>
                    </div>{/* end rounded wrapper */}
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 relative overflow-hidden" data-testid="cta-section">
                <div className="absolute inset-0 inclusion-gradient-bg opacity-30"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-lexend text-3xl md:text-4xl font-bold mb-6">
                        Ready to Join Our Community?
                    </h2>
                    <p className="text-zinc-300 text-lg max-w-2xl mx-auto mb-8">
                        Whether you're looking for support, want to share your experiences, or represent an organization,
                        there's a place for you here.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link to="/register">
                            <Button className="btn-primary text-base px-8 py-4 h-auto" data-testid="cta-individual-btn">
                                <Users className="w-5 h-5 mr-2" />
                                Join as Member
                            </Button>
                        </Link>
                        <Link to="/register?type=provider">
                            <Button variant="ghost" className="btn-secondary text-base px-8 py-4 h-auto" data-testid="cta-provider-btn">
                                <Building2 className="w-5 h-5 mr-2" />
                                Register as Provider
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </Layout>
    );
}
